/**
 * Account dropdown menu — Profile / Wallet / Buy credits / Reports / Settings / Sign out.
 * Opens on avatar/name click. Closes on outside click or item click.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { User, Wallet, FileText, Settings, LogOut, ChevronDown, CreditCard } from "lucide-react";
import { useT, tr } from "@/lib/i18n";

export const AccountDropdown = ({ onBuyClick }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useT();

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!user) return null;
  const close = () => setOpen(false);
  const go = (path) => { close(); navigate(path); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-sm hover:opacity-80" data-testid="account-trigger">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold" style={{ background: "var(--paper)", color: "var(--brand-green)" }}>
            {(user.name || user.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <span className="hidden sm:inline md:hidden lg:inline" style={{ color: "var(--charcoal)" }}>{user.name?.split(" ")[0] || user.email?.split("@")[0]}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-30 w-64 p-2 rounded-md shadow-xl" style={{ background: "var(--white)", border: "1px solid var(--hairline)" }} data-testid="account-dropdown">
          <div className="p-3 border-b" style={{ borderColor: "var(--hairline)" }}>
            <div className="text-sm font-semibold">{user.name || user.email}</div>
            <div className="text-xs truncate" style={{ color: "var(--muted)" }}>{user.email}</div>
          </div>
          <div className="py-1">
            <DdItem icon={User}       label={tr("Profile", "Profil")}                                           testid="dd-profile"  onClick={() => go("/profile")} />
            <DdItem icon={Wallet}     label={tr(`Report-credit wallet · ${user.credits || 0}`, `Portefeuille de crédits · ${user.credits || 0}`)}       testid="dd-wallet"   onClick={() => { close(); toast.info(tr(`You have ${user.credits || 0} report credit${(user.credits || 0) === 1 ? "" : "s"}`, `Vous avez ${user.credits || 0}\u00a0crédit${(user.credits || 0) <= 1 ? "" : "s"} de rapport`)); }} />
            <DdItem icon={CreditCard} label={tr("Buy report credits", "Acheter des crédits")}                                testid="dd-buy"      onClick={() => { close(); onBuyClick && onBuyClick(); }} />
            <DdItem icon={FileText}   label={tr("My unlocked reports", "Mes rapports déverrouillés")}                               testid="dd-reports"  onClick={() => go("/reports")} />
            <DdItem icon={Settings}   label={tr("Profile settings", "Paramètres du profil")}                                  testid="dd-settings" onClick={() => go("/profile/settings")} />
            <div className="my-1 border-t" style={{ borderColor: "var(--hairline)" }} />
            <DdItem icon={LogOut}     label={tr("Sign out", "Déconnexion")}                                          testid="dd-signout"  onClick={() => { close(); logout(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

const DdItem = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-[var(--paper)]" data-testid={testid}>
    <Icon size={13} style={{ color: "var(--muted)" }} />
    {label}
  </button>
);
